import * as React from 'react'
import { Command as CommandPrimitive } from 'cmdk'
import { toast } from 'sonner'
import { AnimatePresence, motion } from 'motion/react'
import {
  Paperclip,
  ArrowUp,
  Square,
  Check,
  DatabaseZap,
  ChevronDown,
  AlertCircle,
  FolderOpen,
  Mic,
  LoaderCircle,
  X,
  Zap,
  Brain,
} from 'lucide-react'
import { Icon_Home, Icon_Folder, Spinner } from '@craft-agent/ui'

import { extractWorkspaceSlugFromPath } from '@craft-agent/shared/utils/workspace-slug'
import { classifyAttachment, bytesToBase64 } from '@craft-agent/shared/utils'
import { getRecentWorkingDirs, addRecentWorkingDir, removeRecentWorkingDir } from './working-directory-history'

import { Button } from '@/components/ui/button'
import {
  InlineSlashCommand,
  useInlineSlashCommand,
  type SlashCommandId,
} from '@/components/ui/slash-command-menu'
import {
  InlineMentionMenu,
  useInlineMention,
  type MentionItem,
  type MentionItemType,
} from '@/components/ui/mention-menu'
import {
  InlineLabelMenu,
  useInlineLabelMenu,
} from '@/components/ui/label-menu'
import type { LabelConfig } from '@craft-agent/shared/labels'
import { SHOW_STATUS } from '@/config/feature-flags'
import { parseMentions } from '@/lib/mentions'
import { RichTextInput, type RichTextInputHandle } from '@/components/ui/rich-text-input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@craft-agent/ui'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu'
import {
  StyledDropdownMenuContent,
  StyledDropdownMenuItem,
  StyledDropdownMenuSeparator,
  StyledDropdownMenuSubTrigger,
  StyledDropdownMenuSubContent,
} from '@/components/ui/styled-dropdown'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { isMac, isIPad, PATH_SEP, getPathBasename } from '@/lib/platform'
import { applySmartTypography } from '@/lib/smart-typography'
import { AttachmentPreview } from '../AttachmentPreview'
import { ANTHROPIC_MODELS, getModelShortName, getModelDisplayName, getModelContextWindow, type ModelDefinition } from '@config/models'
import { MANAGED_MODEL_PRESETS, resolveEffectiveConnectionSlug, isCompatProvider, type ManagedModelPresetId } from '@config/llm-connections'
import { useOptionalAppShellContext } from '@/context/AppShellContext'
import { EditPopover, getEditConfig } from '@/components/ui/EditPopover'
import { SourceAvatar } from '@/components/ui/source-avatar'
import { SourceSelectorPopover } from '@/components/ui/SourceSelectorPopover'
import { ConnectionIcon } from '@/components/icons/ConnectionIcon'
import { FreeFormInputContextBadge } from './FreeFormInputContextBadge'
import { SkillAvatar } from '@/components/ui/skill-avatar'
import { AGENTS_PLUGIN_NAME, getAllSkillIdentifiers, resolveSkillIdentifier } from '@craft-agent/shared/skills/types'
import type { FileAttachment, LoadedSource, LoadedSkill } from '@/shared/types'
import type { PermissionMode } from '@craft-agent/shared/agent/modes'
import { type ThinkingLevel, THINKING_LEVELS, getThinkingLevelName } from '@craft-agent/shared/agent/thinking-levels'
import { useEscapeInterrupt } from '@/context/EscapeInterruptContext'
import { useSpeechToText } from '@/hooks/useSpeechToText'
import { hasOpenOverlay } from '@/lib/overlay-detection'
import { ToolbarStatusSlot } from './ToolbarStatusSlot'
import { ContextRing, formatTokenCount } from './ContextRing'
import { buildPlanApprovalMessage } from '../plan-approval-message'
import { shouldHandleScopedInputEvent } from './input-event-guards'
import { clearPendingFocusForSession, consumePendingFocusForSession } from './focus-input-events'


function stripPiPrefixForDisplay(value: string): string {
  return value.startsWith('pi/') ? value.slice(3) : value
}

function formatFollowUpChipText(text: string, fallback: string, maxLength = 50): string {
  const normalized = text.replace(/\s+/g, ' ').trim()
  if (!normalized) return fallback

  return normalized.length > maxLength
    ? `${normalized.slice(0, maxLength - 1).trimEnd()}…`
    : normalized
}


/** Platform-specific modifier key for keyboard shortcuts */
const cmdKey = isMac ? '⌘' : 'Ctrl'

/** Default rotating placeholders for onboarding/empty state */
const DEFAULT_PLACEHOLDERS = [
  'What would you like to work on?',
  'Use Shift + Tab to switch between Explore and Execute',
  'Type @ to mention files, folders, or skills',
  'Type # to apply labels to this conversation',
  'Press Shift + Return to add a new line',
  `Press ${cmdKey} + B to toggle the sidebar`,
  `Press ${cmdKey} + . for focus mode`,
  `Press ${cmdKey}+Shift+M for voice input`,
]

/** Fisher-Yates shuffle — returns a new array in random order */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

export interface FollowUpInputItem {
  id: string
  messageId: string
  annotationId: string
  index?: number
  noteLabel: string
  selectedText: string
  color?: string
}

export interface FreeFormInputProps {
  /** Placeholder text(s) for the textarea - can be array for rotation */
  placeholder?: string | string[]
  /** Whether input is disabled */
  disabled?: boolean
  /** Whether the session is currently processing */
  isProcessing?: boolean
  /** Callback when message is submitted (skillSlugs from @mentions). delivery is set only while a turn is running. */
  onSubmit: (message: string, attachments?: FileAttachment[], skillSlugs?: string[], delivery?: 'steer' | 'interrupt') => void | Promise<void>
  /** Callback to stop processing. Pass silent=true to skip "Response interrupted" message */
  onStop?: (silent?: boolean) => void
  /** External ref for the input */
  inputRef?: React.RefObject<RichTextInputHandle>
  /** Current model ID */
  currentModel: string
  /** Callback when model changes (includes connection slug for proper persistence) */
  onModelChange: (model: string, connection?: string) => void
  // Thinking level (session-level setting)
  /** Current thinking level ('off', 'think', 'max') */
  thinkingLevel?: ThinkingLevel
  /** Callback when thinking level changes */
  onThinkingLevelChange?: (level: ThinkingLevel) => void
  // Advanced options
  permissionMode?: PermissionMode
  onPermissionModeChange?: (mode: PermissionMode) => void
  /** Enabled permission modes for Shift+Tab cycling (min 2 modes) */
  enabledModes?: PermissionMode[]
  // Controlled input value (for persisting across mode switches and conversation changes)
  /** Current input value - if provided, component becomes controlled */
  inputValue?: string
  /** Callback when input value changes */
  onInputChange?: (value: string) => void
  /** When true, removes container styling (shadow, bg, rounded) - used when wrapped by InputContainer */
  unstyled?: boolean
  /** Callback when component height changes (for external animation sync) */
  onHeightChange?: (height: number) => void
  /** Callback when focus state changes */
  onFocusChange?: (focused: boolean) => void
  // Source selection
  /** Available sources (enabled only) */
  sources?: LoadedSource[]
  /** Currently enabled source slugs for this session */
  enabledSourceSlugs?: string[]
  /** Callback when source selection changes */
  onSourcesChange?: (slugs: string[]) => void
  // Skill selection (for @mentions)
  /** Available skills for @mention autocomplete */
  skills?: LoadedSkill[]
  // Label selection (for #labels)
  /** Available labels for #label autocomplete */
  labels?: LabelConfig[]
  /** Currently applied session labels */
  sessionLabels?: string[]
  /** Callback when a label is added via # menu */
  onLabelAdd?: (labelId: string) => void
  /** Workspace ID for loading skill icons */
  workspaceId?: string
  /** Current working directory path */
  workingDirectory?: string
  /** Callback when working directory changes */
  onWorkingDirectoryChange?: (path: string) => void
  /** Session folder path (for "Reset to Session Root" option) */
  sessionFolderPath?: string
  /** Session ID for scoping events like approve-plan */
  sessionId?: string
  /** Current session status of the session (for # menu state selection) */
  currentSessionStatus?: string
  /** Disable send action (for tutorial guidance) */
  disableSend?: boolean
  /** Whether the session is empty (no messages yet) - affects context badge prominence */
  isEmptySession?: boolean
  /** Context status for showing compaction indicator and token usage */
  contextStatus?: {
    /** True when SDK is actively compacting the conversation */
    isCompacting?: boolean
    /** Input tokens used so far in this session */
    inputTokens?: number
    /** Model's context window size in tokens */
    contextWindow?: number
  }
  /** Follow-up annotations shown as context chips above the input */
  followUpItems?: FollowUpInputItem[]
  /** Callback when user clicks a follow-up chip body */
  onFollowUpClick?: (item: FollowUpInputItem, anchor?: { x: number; y: number }) => void
  /** Callback when user clicks the follow-up index badge */
  onFollowUpIndexClick?: (item: FollowUpInputItem) => void
  /** Enable compact mode - hides attach, sources, working directory for popover embedding */
  compactMode?: boolean
  // Connection selection (hierarchical connection → model selector)
  /** Current LLM connection slug (locked after first message) */
  currentConnection?: string
  /** Callback when connection changes (only works when session is empty) */
  onConnectionChange?: (connectionSlug: string) => void
  /** When true, the session's locked connection has been removed */
  connectionUnavailable?: boolean
}

/**
 * FreeFormInput - Self-contained textarea input with attachments and controls
 *
 * Features:
 * - Auto-growing textarea
 * - File attachments via button or drag-drop
 * - Slash commands menu
 * - Model selector
 * - Active option badges
 */
export function FreeFormInput({
  placeholder = DEFAULT_PLACEHOLDERS,
  disabled = false,
  isProcessing = false,
  onSubmit,
  onStop,
  inputRef: externalInputRef,
  currentModel,
  onModelChange,
  thinkingLevel = 'think',
  onThinkingLevelChange,
  permissionMode = 'allow-all',
  onPermissionModeChange,
  enabledModes = ['safe', 'allow-all'],
  inputValue,
  onInputChange,
  unstyled = false,
  onHeightChange,
  onFocusChange,
  sources = [],
  enabledSourceSlugs = [],
  onSourcesChange,
  skills = [],
  labels = [],
  sessionLabels = [],
  onLabelAdd,
  workspaceId,
  workingDirectory,
  onWorkingDirectoryChange,
  sessionFolderPath,
  sessionId,
  currentSessionStatus,
  disableSend = false,
  isEmptySession = false,
  contextStatus,
  followUpItems = [],
  onFollowUpClick,
  onFollowUpIndexClick,
  compactMode = false,
  currentConnection,
  onConnectionChange,
  connectionUnavailable = false,
}: FreeFormInputProps) {
  // Read connection default model, connections, and workspace info from context.
  // Uses optional variant so playground (no provider) doesn't crash.
  const appShellCtx = useOptionalAppShellContext()
  const llmConnections = React.useMemo(() => appShellCtx?.llmConnections ?? [], [appShellCtx?.llmConnections])
  const workspaceDefaultConnection = appShellCtx?.workspaceDefaultLlmConnection

  // Derive connectionDefaultModel per-session from the effective connection.
  // Only non-null for compat providers (custom endpoints with fixed models).
  // Standard providers (anthropic, openai, bedrock, vertex) → null → normal model picker.
  const connectionDefaultModel = React.useMemo(() => {
    const effectiveSlug = resolveEffectiveConnectionSlug(currentConnection, workspaceDefaultConnection, llmConnections)
    const conn = llmConnections.find(c => c.slug === effectiveSlug)
    if (!conn) return null
    if (!isCompatProvider(conn.providerType)) return null
    // Allow model switching when connection has multiple models
    if (conn.models && conn.models.length > 1) return null
    return conn.defaultModel ?? null
  }, [currentConnection, workspaceDefaultConnection, llmConnections])

  // Compute available models from the effective connection.
  // All connections have models populated by backfillAllConnectionModels().
  const availableModels = React.useMemo(() => {
    // Connection removed — don't fall through to another connection's models
    if (connectionUnavailable) return []

    // Determine effective connection using the canonical fallback chain
    const effectiveSlug = resolveEffectiveConnectionSlug(currentConnection, workspaceDefaultConnection, llmConnections)
    const connection = llmConnections.find(c => c.slug === effectiveSlug)

    if (!connection) {
      return ANTHROPIC_MODELS // Safety net — shouldn't happen
    }

    return connection.models || ANTHROPIC_MODELS
  }, [llmConnections, currentConnection, workspaceDefaultConnection, connectionUnavailable])

  const availableThinkingLevels = THINKING_LEVELS

  // Disable thinking selector when the current model explicitly doesn't support it
  const thinkingDisabled = React.useMemo(() => {
    const model = availableModels.find(m => typeof m !== 'string' && m.id === currentModel)
    return typeof model !== 'string' && model?.supportsThinking === false
  }, [availableModels, currentModel])

  // Get display name for current model (full name, not short name)
  const currentModelDisplayName = React.useMemo(() => {
    const modelToDisplay = connectionDefaultModel ?? currentModel
    const model = availableModels.find(m =>
      typeof m === 'string' ? m === modelToDisplay : m.id === modelToDisplay
    )
    if (!model) {
      // Fallback: use helper function to format unknown model IDs nicely
      return stripPiPrefixForDisplay(getModelDisplayName(modelToDisplay))
    }
    return typeof model === 'string' ? stripPiPrefixForDisplay(model) : model.name
  }, [availableModels, currentModel, connectionDefaultModel])

  // Group connections by provider type for hierarchical dropdown
  // Each provider (Anthropic, Pi) can have multiple connections (API Key, OAuth, etc.)
  const connectionsByProvider = React.useMemo(() => {
    const groups: Record<string, typeof llmConnections> = {
      'Anthropic': [],
      'Origin Relay Backend': [],
    }
    for (const conn of llmConnections) {
      const provider = conn.providerType || 'anthropic'
      // Group by SDK: anthropic/anthropic_compat/bedrock/vertex use Anthropic SDK
      if (provider === 'anthropic' || provider === 'anthropic_compat' || provider === 'bedrock' || provider === 'vertex') {
        groups['Anthropic'].push(conn)
      } else if (provider === 'pi' || provider === 'pi_compat') {
        groups['Origin Relay Backend'].push(conn)
      }
    }
    // Return only non-empty groups
    return Object.entries(groups).filter(([, conns]) => conns.length > 0)
  }, [llmConnections])

  // Find current connection details for display
  const currentConnectionDetails = React.useMemo(() => {
    if (!currentConnection) return null
    return llmConnections.find(c => c.slug === currentConnection) ?? null
  }, [llmConnections, currentConnection])

  // Effective connection: canonical fallback chain (session → workspace default → global default → first)
  const effectiveConnection = resolveEffectiveConnectionSlug(currentConnection, workspaceDefaultConnection, llmConnections)

  // Effective connection details (with fallbacks) for model list
  // Unlike currentConnectionDetails which is null when no explicit connection is set,
  // this resolves to the actual connection being used (including workspace default)
  const effectiveConnectionDetails = React.useMemo(() => {
    if (!effectiveConnection) return null
    return llmConnections.find(c => c.slug === effectiveConnection) ?? null
  }, [llmConnections, effectiveConnection])

  const availableManagedPresets = React.useMemo(() => MANAGED_MODEL_PRESETS.filter((preset) => {
    const connection = llmConnections.find((candidate) => candidate.slug === preset.connectionSlug)
    return connection?.isBundled && (connection as typeof connection & { isAuthenticated?: boolean }).isAuthenticated !== false
  }), [llmConnections])


  // Access sessionStatuses and onSessionStatusChange from context for the # menu state picker
  const sessionStatuses = appShellCtx?.sessionStatuses ?? []
  const onSessionStatusChange = appShellCtx?.onSessionStatusChange
  // Resolve workspace rootPath for "Add New Label" deep link
  const workspaceRootPath = React.useMemo(() => {
    if (!appShellCtx || !workspaceId) return null
    return appShellCtx.workspaces.find(w => w.id === workspaceId)?.rootPath ?? null
  }, [appShellCtx, workspaceId])

  // Compute workspace slug from rootPath for SDK skill qualification
  // SDK expects "workspaceSlug:skillSlug" format, NOT UUID
  const workspaceSlug = React.useMemo(() => {
    if (!workspaceRootPath) return workspaceId // Fallback to ID if no path
    return extractWorkspaceSlugFromPath(workspaceRootPath, workspaceId ?? '')
  }, [workspaceRootPath, workspaceId])

  // Read panel focus state from context (for multi-panel unfocused styling)
  const appShellContext = useOptionalAppShellContext()
  const isFocusedPanel = appShellContext?.isFocusedPanel ?? true

  // Shuffle placeholder order once per mount so each session feels fresh
  // Hide placeholder entirely when panel is unfocused in multi-panel layout
  const shuffledPlaceholder = React.useMemo(
    () => Array.isArray(placeholder) ? shuffleArray(placeholder) : placeholder,
    [] // eslint-disable-line react-hooks/exhaustive-deps -- intentionally shuffle only on mount
  )
  const effectivePlaceholder = isFocusedPanel ? shuffledPlaceholder : ''

  // Performance optimization: Always use internal state for typing to avoid parent re-renders
  // Sync FROM parent on mount/change (for restoring drafts)
  // Sync TO parent on blur/submit (debounced persistence)
  const [input, setInput] = React.useState(inputValue ?? '')
  const [attachments, setAttachments] = React.useState<FileAttachment[]>([])
  const [planModeActive, setPlanModeActive] = React.useState(false)

  // Ref to track current attachments for use in event handlers (avoids stale closure issues)
  const attachmentsRef = React.useRef<FileAttachment[]>([])
  React.useEffect(() => {
    attachmentsRef.current = attachments
  }, [attachments])

  // Optimistic state for source selection - updates UI immediately before IPC round-trip completes
  const [optimisticSourceSlugs, setOptimisticSourceSlugs] = React.useState(enabledSourceSlugs)

  // Sync from prop when server state changes (reconciles after IPC or on external updates)
  // Use content comparison (not reference) to avoid infinite loops with empty arrays
  const prevEnabledSourceSlugsRef = React.useRef(enabledSourceSlugs)
  React.useEffect(() => {
    const prev = prevEnabledSourceSlugsRef.current
    const changed = enabledSourceSlugs.length !== prev.length ||
      enabledSourceSlugs.some((slug, i) => slug !== prev[i])

    if (changed) {
      setOptimisticSourceSlugs(enabledSourceSlugs)
      prevEnabledSourceSlugsRef.current = enabledSourceSlugs
    }
  }, [enabledSourceSlugs])

  // Sync from parent when inputValue changes externally (e.g., switching sessions)
  const prevInputValueRef = React.useRef(inputValue)
  React.useEffect(() => {
    if (inputValue !== undefined && inputValue !== prevInputValueRef.current) {
      setInput(inputValue)
      prevInputValueRef.current = inputValue
    }
  }, [inputValue])

  // Debounced sync to parent (saves draft without blocking typing)
  const syncTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const syncToParent = React.useCallback((value: string) => {
    if (!onInputChange) return
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current)
    syncTimeoutRef.current = setTimeout(() => {
      onInputChange(value)
      prevInputValueRef.current = value
    }, 300) // Debounce 300ms
  }, [onInputChange])

  // Sync immediately on unmount to preserve input across mode switches
  // Also cleanup any pending debounced sync
  const inputRef = React.useRef(input)
  inputRef.current = input // Keep ref in sync with state

  React.useEffect(() => {
    return () => {
      // Cancel pending debounced sync
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current)
      // Immediately sync current value to parent on unmount
      // This preserves input when switching to structured input (e.g., permission request)
      if (onInputChange && inputRef.current !== prevInputValueRef.current) {
        onInputChange(inputRef.current)
      }
    }
  }, [onInputChange])

  const [isDraggingOver, setIsDraggingOver] = React.useState(false)
  const [loadingCount, setLoadingCount] = React.useState(0)
  const [sourceDropdownOpen, setSourceDropdownOpen] = React.useState(false)
  const [skillDropdownOpen, setSkillDropdownOpen] = React.useState(false)
  const [isFocused, setIsFocused] = React.useState(false)
  const [inputMaxHeight, setInputMaxHeight] = React.useState(540)
  const [modelDropdownOpen, setModelDropdownOpen] = React.useState(false)
  const [isApplyingModelPreset, setIsApplyingModelPreset] = React.useState(false)
  const [optimisticPresetId, setOptimisticPresetId] = React.useState<ManagedModelPresetId | null>(null)

  React.useEffect(() => {
    setOptimisticPresetId(null)
    setIsApplyingModelPreset(false)
  }, [sessionId])

  const selectedManagedPreset = React.useMemo(() => (
    MANAGED_MODEL_PRESETS.find((preset) => preset.id === optimisticPresetId)
      ?? MANAGED_MODEL_PRESETS.find((preset) => preset.connectionSlug === effectiveConnection)
  ), [optimisticPresetId, effectiveConnection])

  const applyManagedModelPreset = React.useCallback(async (presetId: ManagedModelPresetId) => {
    if (!sessionId || isApplyingModelPreset) return
    setOptimisticPresetId(presetId)
    setIsApplyingModelPreset(true)
    try {
      await window.electronAPI.sessionCommand(sessionId, { type: 'applyModelPreset', presetId })
      setModelDropdownOpen(false)
    } catch (error) {
      setOptimisticPresetId(null)
      toast.error(error instanceof Error ? error.message : 'Failed to change model provider')
    } finally {
      setIsApplyingModelPreset(false)
    }
  }, [sessionId, isApplyingModelPreset])

  // Input settings (loaded from config)
  const [autoCapitalisation, setAutoCapitalisation] = React.useState(true)
  const [sendMessageKey, setSendMessageKey] = React.useState<'enter' | 'cmd-enter'>('enter')
  const [spellCheck, setSpellCheck] = React.useState(false)

  // Load input settings on mount
  React.useEffect(() => {
    const loadInputSettings = async () => {
      if (!window.electronAPI) return
      try {
        const [autoCapEnabled, sendKey, spellCheckEnabled] = await Promise.all([
          window.electronAPI.getAutoCapitalisation(),
          window.electronAPI.getSendMessageKey(),
          window.electronAPI.getSpellCheck(),
        ])
        setAutoCapitalisation(autoCapEnabled)
        setSendMessageKey(sendKey ?? 'enter')
        setSpellCheck(spellCheckEnabled)
      } catch (error) {
        console.error('Failed to load input settings:', error)
      }
    }
    loadInputSettings()
  }, [])

  // Double-Esc interrupt: show warning overlay on first Esc, interrupt on second
  const { showEscapeOverlay } = useEscapeInterrupt()

  // Speech-to-text: mic recording + transcription
  const { isRecording, isTranscribing, toggleRecording, error: sttError } = useSpeechToText()

  // Recording duration timer
  const [recordingDuration, setRecordingDuration] = React.useState(0)
  const recordingTimerRef = React.useRef<ReturnType<typeof setInterval>>()
  React.useEffect(() => {
    if (isRecording) {
      setRecordingDuration(0)
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(d => d + 1)
      }, 1000)
    } else {
      clearInterval(recordingTimerRef.current)
      setRecordingDuration(0)
    }
    return () => clearInterval(recordingTimerRef.current)
  }, [isRecording])

  // Calculate max height: min(66% of window height, 540px)
  React.useEffect(() => {
    const updateMaxHeight = () => {
      const maxFromWindow = Math.floor(window.innerHeight * 0.66)
      setInputMaxHeight(Math.min(maxFromWindow, 540))
    }
    updateMaxHeight()
    window.addEventListener('resize', updateMaxHeight)
    return () => window.removeEventListener('resize', updateMaxHeight)
  }, [])

  const dragCounterRef = React.useRef(0)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const sourceButtonRef = React.useRef<HTMLButtonElement>(null)

  // Merge refs for RichTextInput
  const internalInputRef = React.useRef<RichTextInputHandle>(null)
  const richInputRef = externalInputRef || internalInputRef

  // Track last caret position for focus restoration (e.g., after permission mode popover closes)
  const lastCaretPositionRef = React.useRef<number | null>(null)

  // Listen for craft:insert-text events (generic mechanism for inserting text into input)
  // Used by components that want to pre-fill the input with text
  React.useEffect(() => {
    const handleInsertText = (e: CustomEvent<{ text: string; sessionId?: string }>) => {
      const targetSessionId = e.detail?.sessionId
      if (!shouldHandleScopedInputEvent({ sessionId, isFocusedPanel, targetSessionId })) return

      const { text } = e.detail
      setInput(text)
      syncToParent(text)
      // Focus the input after inserting
      setTimeout(() => {
        richInputRef.current?.focus()
        // Move cursor to end
        richInputRef.current?.setSelectionRange(text.length, text.length)
      }, 0)
    }

    window.addEventListener('craft:insert-text', handleInsertText as EventListener)
    return () => window.removeEventListener('craft:insert-text', handleInsertText as EventListener)
  }, [sessionId, isFocusedPanel, syncToParent, richInputRef])

  const clearInputDraft = React.useCallback(() => {
    setInput('')
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current)
    onInputChange?.('')
    prevInputValueRef.current = ''
  }, [onInputChange])

  const consumeInputDraftSnapshot = React.useCallback((): string => {
    const snapshot = input.trim()
    clearInputDraft()
    return snapshot
  }, [input, clearInputDraft])

  type PlanApprovalEventDetail = {
    sessionId?: string
    planPath?: string
    includeDraftInput?: boolean
    source?: string
  }

  // Listen for craft:approve-plan events (used by ResponseCard's Accept Plan button)
  // This disables safe mode AND submits the message in one action
  // Only process events for this session (sessionId must match)
  React.useEffect(() => {
    const handleApprovePlan = (e: CustomEvent<PlanApprovalEventDetail>) => {
      // Only handle if this event is for our session
      if (e.detail?.sessionId && e.detail.sessionId !== sessionId) {
        return
      }

      const shouldIncludeDraft = e.detail?.includeDraftInput !== false
      const draftInput = shouldIncludeDraft ? consumeInputDraftSnapshot() : ''
      const text = buildPlanApprovalMessage({
        planPath: e.detail?.planPath,
        draftInput,
      })

      // Switch to allow-all (Auto) mode if in Explore mode (allow execution without prompts)
      // Only switch if currently in safe mode - if user is in 'ask' mode, respect their choice
      if (permissionMode === 'safe') {
        onPermissionModeChange?.('allow-all')
      }

      onSubmit(text, undefined)
    }

    window.addEventListener('craft:approve-plan', handleApprovePlan as EventListener)
    return () => window.removeEventListener('craft:approve-plan', handleApprovePlan as EventListener)
  }, [sessionId, permissionMode, onPermissionModeChange, onSubmit, consumeInputDraftSnapshot])

  // Listen for craft:approve-plan-with-compact events (Accept & Compact option)
  // This compacts the conversation first, then executes the plan.
  // The pending state is persisted to survive page reloads (CMD+R).
  React.useEffect(() => {
    const handleApprovePlanWithCompact = async (e: CustomEvent<PlanApprovalEventDetail>) => {
      // Only handle if this event is for our session
      if (e.detail?.sessionId && e.detail.sessionId !== sessionId) {
        return
      }

      const planPath = e.detail?.planPath
      const shouldIncludeDraft = e.detail?.includeDraftInput !== false
      const draftInputSnapshot = shouldIncludeDraft ? consumeInputDraftSnapshot() : ''

      // Switch to allow-all (Auto) mode if in Explore mode
      if (permissionMode === 'safe') {
        onPermissionModeChange?.('allow-all')
      }

      // Persist the pending plan execution state BEFORE sending /compact.
      // This allows reload recovery if CMD+R happens during compaction.
      if (sessionId) {
        try {
          await window.electronAPI.sessionCommand(sessionId, {
            type: 'setPendingPlanExecution',
            planPath: planPath ?? '',
            draftInputSnapshot,
          })
        } catch {
          // Best-effort persistence for reload recovery
        }
      }

      // Send /compact to trigger compaction
      onSubmit('/compact', undefined)

      // Set up a one-time listener for compaction complete.
      // This handles the normal case (no reload during compaction).
      const handleCompactionComplete = async (compactEvent: CustomEvent<{ sessionId?: string }>) => {
        // Only handle if this is for our session
        if (compactEvent.detail?.sessionId !== sessionId) {
          return
        }

        // Remove the listener (one-time use)
        window.removeEventListener('craft:compaction-complete', handleCompactionComplete as unknown as EventListener)

        const executionMessage = buildPlanApprovalMessage({
          planPath,
          draftInput: draftInputSnapshot,
        })
        onSubmit(executionMessage, undefined)

        // Clear the pending state since we just sent the execution message
        if (sessionId) {
          try {
            await window.electronAPI.sessionCommand(sessionId, {
              type: 'clearPendingPlanExecution',
            })
          } catch {
            // Best-effort cleanup
          }
        }
      }

      window.addEventListener('craft:compaction-complete', handleCompactionComplete as unknown as EventListener)
    }

    window.addEventListener('craft:approve-plan-with-compact', handleApprovePlanWithCompact as unknown as EventListener)
    return () => window.removeEventListener('craft:approve-plan-with-compact', handleApprovePlanWithCompact as unknown as EventListener)
  }, [sessionId, permissionMode, onPermissionModeChange, onSubmit, consumeInputDraftSnapshot])

  // Reload recovery: Check for pending plan execution on mount.
  // If the page reloaded after compaction completed (awaitingCompaction = false),
  // we need to send the plan execution message that was interrupted by the reload.
  // Also listen for compaction-complete in case CMD+R happened during compaction.
  React.useEffect(() => {
    if (!sessionId) return

    let hasExecuted = false

    const executePendingPlan = async () => {
      if (hasExecuted) return
      if (!window.electronAPI) return

      try {
        const pending = await window.electronAPI.getPendingPlanExecution(sessionId)
        if (!pending || pending.awaitingCompaction) return

        // Compaction completed but we never sent the execution message (page reloaded).
        // Send it now and clear the pending state.
        hasExecuted = true
        const executionMessage = buildPlanApprovalMessage({
          planPath: pending.planPath,
          draftInput: pending.draftInputSnapshot,
        })
        onSubmit(executionMessage, undefined)

        await window.electronAPI.sessionCommand(sessionId, {
          type: 'clearPendingPlanExecution',
        })
      } catch {
        /* Best-effort reload recovery */
      }
    }

    // Check immediately on mount (handles case where compaction already completed)
    executePendingPlan()

    // Also listen for compaction-complete in case CMD+R happened during compaction.
    // When compaction finishes after reload, this listener will trigger execution.
    const handleCompactionComplete = async (e: CustomEvent<{ sessionId: string }>) => {
      if (e.detail?.sessionId !== sessionId) return
      // Small delay to ensure markCompactionComplete has been called
      await new Promise(resolve => setTimeout(resolve, 100))
      executePendingPlan()
    }

    window.addEventListener('craft:compaction-complete', handleCompactionComplete as unknown as EventListener)
    return () => {
      window.removeEventListener('craft:compaction-complete', handleCompactionComplete as unknown as EventListener)
    }
  }, [sessionId, onSubmit])

  // Listen for craft:focus-input events (restore focus after popover/dropdown closes)
  React.useEffect(() => {
    const handleFocusInput = (e: Event) => {
      const detail = (e as CustomEvent<{ sessionId?: string }>).detail
      const targetSessionId = detail?.sessionId
      if (!shouldHandleScopedInputEvent({ sessionId, isFocusedPanel, targetSessionId })) return

      if (targetSessionId) {
        clearPendingFocusForSession(targetSessionId)
      }

      richInputRef.current?.focus()
      // Restore caret position if saved, then clear it (one-shot)
      if (lastCaretPositionRef.current !== null) {
        richInputRef.current?.setSelectionRange(
          lastCaretPositionRef.current,
          lastCaretPositionRef.current
        )
        lastCaretPositionRef.current = null
      }
    }

    window.addEventListener('craft:focus-input', handleFocusInput)
    return () => window.removeEventListener('craft:focus-input', handleFocusInput)
  }, [sessionId, isFocusedPanel, richInputRef])

  // Recover queued focus requests after session switch/mount races.
  React.useEffect(() => {
    if (!consumePendingFocusForSession(sessionId)) return

    setTimeout(() => {
      richInputRef.current?.focus()
    }, 0)
  }, [sessionId, richInputRef])

  // Get the next available number for a pasted file prefix (e.g., pasted-image-1, pasted-image-2)
  const getNextPastedNumber = (
    prefix: 'image' | 'text' | 'file',
    existingAttachments: FileAttachment[]
  ): number => {
    const pattern = new RegExp(`^pasted-${prefix}-(\\d+)\\.`)
    let maxNum = 0
    for (const att of existingAttachments) {
      const match = att.name.match(pattern)
      if (match) {
        maxNum = Math.max(maxNum, parseInt(match[1], 10))
      }
    }
    return maxNum + 1
  }

  // Listen for craft:paste-files events (for global paste when input not focused)
  React.useEffect(() => {
    const handlePasteFiles = async (e: CustomEvent<{ files: File[]; sessionId?: string }>) => {
      if (disabled) return

      const targetSessionId = e.detail?.sessionId
      if (!shouldHandleScopedInputEvent({ sessionId, isFocusedPanel, targetSessionId })) return

      const { files } = e.detail
      if (!files || files.length === 0) return

      setLoadingCount(prev => prev + files.length)

      // Pre-assign sequential names using ref to avoid race conditions
      let nextImageNum = getNextPastedNumber('image', attachmentsRef.current)
      const fileNames: string[] = files.map(file => {
        if (!file.name || file.name === 'image.png' || file.name === 'image.jpg' || file.name === 'blob') {
          const ext = file.type.split('/')[1] || 'png'
          return `pasted-image-${nextImageNum++}.${ext}`
        }
        return file.name
      })

      for (let i = 0; i < files.length; i++) {
        try {
          const attachment = await readFileAsAttachment(files[i], fileNames[i])
          if (attachment) {
            setAttachments(prev => [...prev, attachment])
          }
        } catch (error) {
          console.error('[FreeFormInput] Failed to process pasted file:', error)
        }
        setLoadingCount(prev => prev - 1)
      }

      // Focus the input after adding attachments
      richInputRef.current?.focus()
    }

    window.addEventListener('craft:paste-files', handlePasteFiles as unknown as EventListener)
    return () => window.removeEventListener('craft:paste-files', handlePasteFiles as unknown as EventListener)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- readFileAsAttachment is intentionally excluded; richInputRef is a stable ref
  }, [disabled, sessionId, isFocusedPanel, richInputRef])

  // Build active commands list for slash command menu
  const activeCommands = React.useMemo(() => {
    const active: SlashCommandId[] = []
    // Add the currently active permission mode
    if (permissionMode === 'safe') active.push('safe')
    else if (permissionMode === 'allow-all') active.push('allow-all')
    return active
  }, [permissionMode])

  // Handle slash command selection (mode/feature commands)
  const handleSlashCommand = React.useCallback((commandId: SlashCommandId) => {
    if (commandId === 'safe') onPermissionModeChange?.('safe')
    else if (commandId === 'allow-all') onPermissionModeChange?.('allow-all')
    else if (commandId === 'compact' && !isProcessing) onSubmit('/compact', undefined)
  }, [onPermissionModeChange, isProcessing, onSubmit])

  // Handle folder selection from slash command menu
  const handleSlashFolderSelect = React.useCallback((path: string) => {
    if (onWorkingDirectoryChange) {
      setRecentFolders(addRecentWorkingDir(path))
      onWorkingDirectoryChange(path)
    }
  }, [onWorkingDirectoryChange])

  // Get recent folders and home directory for slash menu and mention menu
  const [recentFolders, setRecentFolders] = React.useState<string[]>([])
  const [homeDir, setHomeDir] = React.useState<string>('')

  React.useEffect(() => {
    setRecentFolders(getRecentWorkingDirs())
    window.electronAPI?.getHomeDir?.().then((dir: string) => {
      if (dir) setHomeDir(dir)
    }).catch(() => {})
  }, [])

  // Inline slash command hook (modes, features, and folders)
  const inlineSlash = useInlineSlashCommand({
    inputRef: richInputRef,
    onSelectCommand: handleSlashCommand,
    onSelectFolder: handleSlashFolderSelect,
    activeCommands,
    recentFolders,
    homeDir,
  })

  // Handle mention selection (sources, skills, files)
  const handleMentionSelect = React.useCallback((item: MentionItem) => {
    // For sources: enable the source immediately
    if (item.type === 'source' && item.source && onSourcesChange) {
      const slug = item.source.config.slug
      if (!optimisticSourceSlugs.includes(slug)) {
        const newSlugs = [...optimisticSourceSlugs, slug]
        setOptimisticSourceSlugs(newSlugs)
        onSourcesChange(newSlugs)
      }
    }

    // Files via @ mention in text are sufficient context for the agent.
    // Skills also don't need special handling beyond text insertion.
  }, [optimisticSourceSlugs, onSourcesChange])

  // Inline mention hook (for skills, sources, and files)
  const inlineMention = useInlineMention({
    inputRef: richInputRef,
    skills,
    sources,
    basePath: workingDirectory,
    onSelect: handleMentionSelect,
    // Use workspace slug (not UUID) for SDK skill qualification
    workspaceId: workspaceSlug,
  })

  // Inline label menu hook (for #labels)
  const handleLabelSelect = React.useCallback((labelId: string) => {
    onLabelAdd?.(labelId)
  }, [onLabelAdd])

  const inlineLabel = useInlineLabelMenu({
    inputRef: richInputRef,
    labels,
    sessionLabels,
    onSelect: handleLabelSelect,
    sessionStatuses: SHOW_STATUS ? sessionStatuses : [],
    activeStateId: SHOW_STATUS ? currentSessionStatus : undefined,
  })

  // "Add New Label" handler: cleans up the #trigger text and opens a controlled
  // EditPopover so the user can describe the label before the agent creates it.
  const [addLabelPopoverOpen, setAddLabelPopoverOpen] = React.useState(false)
  const [addLabelPrefill, setAddLabelPrefill] = React.useState('')
  const handleAddLabel = React.useCallback((prefill: string) => {
    if (!workspaceRootPath) return

    // Remove the #trigger text from input
    const cleaned = inlineLabel.handleSelect('')
    setInput(cleaned)
    syncToParent(cleaned)
    inlineLabel.close()

    // Store the prefill text (e.g., "Test" from "#Test") to pre-fill the popover
    // Format: "Add new label {prefill}" so user can just press enter or modify
    setAddLabelPrefill(prefill ? `Add new label ${prefill}` : '')

    // Open the EditPopover for label creation
    setAddLabelPopoverOpen(true)
  }, [workspaceRootPath, inlineLabel, syncToParent])

  // Memoize the add-label config so the EditPopover doesn't recreate on every render
  const addLabelEditConfig = React.useMemo(() => {
    if (!workspaceRootPath) return null
    return getEditConfig('add-label', workspaceRootPath)
  }, [workspaceRootPath])

  // Report height changes to parent (for external animation sync)
  React.useLayoutEffect(() => {
    if (!onHeightChange || !containerRef.current) return

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        onHeightChange(entry.contentRect.height)
      }
    })

    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [onHeightChange])

  // In compact mode, immediately report collapsed height when processing state changes
  // This ensures smooth animation timing when input collapses/expands
  React.useEffect(() => {
    if (!onHeightChange || !compactMode) return
    if (isProcessing) {
      // Collapsed state - only bottom bar visible (~44px)
      onHeightChange(44)
    }
    // When not processing, ResizeObserver will report the full height
  }, [compactMode, isProcessing, onHeightChange])

  // Check if running in Electron environment (has electronAPI)
  const hasElectronAPI = typeof window !== 'undefined' && !!window.electronAPI

  // Hidden file input ref for browser-based file picker (remote client)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // File attachment handlers — use browser file picker on remote client
  const handleAttachClick = async () => {
    if (disabled) return
    // Use hidden <input type="file"> to open browser file picker
    fileInputRef.current?.click()
  }

  // Handle files selected via browser file picker
  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0) return

    setLoadingCount(prev => prev + files.length)

    for (const file of files) {
      try {
        const attachment = await readFileAsAttachment(file)
        if (attachment) {
          setAttachments(prev => [...prev, attachment])
        }
      } catch (error) {
        console.error('[FreeFormInput] Failed to read attached file:', error)
      }
      setLoadingCount(prev => prev - 1)
    }

    // Reset the input so the same file can be re-selected
    e.target.value = ''
    richInputRef.current?.focus()
  }

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
  }

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current++
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) {
      setIsDraggingOver(false)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  // Helper to read a File using FileReader API (remote / clipboard / no filesystem path)
  const readFileAsAttachment = async (file: File, overrideName?: string): Promise<FileAttachment | null> => {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = async () => {
        try {
          const result = reader.result as ArrayBuffer
          const bytes = new Uint8Array(result)
          const base64 = bytesToBase64(bytes)

          const fileName = overrideName || file.name
          const mimeType = file.type || 'application/octet-stream'
          const type = classifyAttachment(fileName, file.type)

          let thumbnailBase64: string | undefined
          if (hasElectronAPI && type === 'image') {
            try {
              const thumb = await window.electronAPI.generateThumbnail(base64, mimeType)
              if (thumb) thumbnailBase64 = thumb
            } catch {
              // Thumbnail generation is optional, continue without it
            }
          }

          resolve({
            type,
            path: fileName,
            name: fileName,
            mimeType,
            base64,
            size: file.size,
            thumbnailBase64,
          })
        } catch (error) {
          console.error('[FreeFormInput] Failed to encode attachment:', error)
          resolve(null)
        }
      }
      reader.onerror = () => resolve(null)
      reader.readAsArrayBuffer(file)
    })
  }

  // Clipboard paste handler for files/images
  const handlePaste = async (e: React.ClipboardEvent) => {
    if (disabled) return

    const clipboardItems = e.clipboardData?.files
    if (!clipboardItems || clipboardItems.length === 0) return

    // We have files to process - prevent default text paste behavior
    e.preventDefault()

    const files = Array.from(clipboardItems)
    setLoadingCount(prev => prev + files.length)

    // Pre-assign sequential names using ref to avoid race conditions
    let nextImageNum = getNextPastedNumber('image', attachmentsRef.current)
    const fileNames: string[] = files.map(file => {
      if (!file.name || file.name === 'image.png' || file.name === 'image.jpg' || file.name === 'blob') {
        const ext = file.type.split('/')[1] || 'png'
        return `pasted-image-${nextImageNum++}.${ext}`
      }
      return file.name
    })

    for (let i = 0; i < files.length; i++) {
      try {
        const attachment = await readFileAsAttachment(files[i], fileNames[i])
        if (attachment) {
          setAttachments(prev => [...prev, attachment])
        }
      } catch (error) {
        console.error('[FreeFormInput] Failed to read pasted file:', error)
      }
      setLoadingCount(prev => prev - 1)
    }
  }

  // Handle long text paste - convert to file attachment
  const handleLongTextPaste = React.useCallback((text: string) => {
    const nextNum = getNextPastedNumber('text', attachmentsRef.current)
    const fileName = `pasted-text-${nextNum}.txt`
    const attachment: FileAttachment = {
      type: 'text',
      path: fileName,
      name: fileName,
      mimeType: 'text/plain',
      text: text,
      size: new Blob([text]).size,
    }
    setAttachments(prev => [...prev, attachment])
    // Focus input after adding attachment
    richInputRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- richInputRef is a stable ref
  }, [])

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current = 0
    setIsDraggingOver(false)
    if (disabled) return

    const files = Array.from(e.dataTransfer.files)
    setLoadingCount(files.length)

    for (const file of files) {
      const filePath = (hasElectronAPI && typeof window.electronAPI.getPathForFile === 'function'
        ? window.electronAPI.getPathForFile(file)
        : '') || (file as File & { path?: string }).path
      if (filePath && hasElectronAPI) {
        try {
          const attachment = await window.electronAPI.readFileAttachment(filePath)
          if (attachment) {
            setAttachments(prev => [...prev, attachment])
            setLoadingCount(prev => prev - 1)
            continue
          }
        } catch (error) {
          console.error('[FreeFormInput] Failed to read via IPC:', error)
        }
      }

      try {
        const attachment = await readFileAsAttachment(file)
        if (attachment) {
          setAttachments(prev => [...prev, attachment])
        }
      } catch (error) {
        console.error('[FreeFormInput] Failed to read dropped file:', error)
      }
      setLoadingCount(prev => prev - 1)
    }
  }

  // Submit message. While a turn is running, delivery chooses steer vs stop-and-send.
  const submitMessage = React.useCallback((delivery?: 'steer' | 'interrupt') => {
    const hasContent = input.trim() || attachments.length > 0 || followUpItems.length > 0
    if (!hasContent || disabled) return false

    // Tutorial may disable sending to guide user through specific steps
    if (disableSend || isApplyingModelPreset) return false

    // Parse all @mentions (skills, sources, folders)
    const skillIdentifiers = getAllSkillIdentifiers(skills)
    const sourceSlugs = sources.map(s => s.config.slug)
    const mentions = parseMentions(input, skillIdentifiers, sourceSlugs)
    const resolvedSkillSlugs = Array.from(new Set(
      mentions.skills
        .map(identifier => resolveSkillIdentifier(skills, identifier)?.slug)
        .filter((slug): slug is string => Boolean(slug))
    ))

    // Enable any mentioned sources that aren't already enabled
    if (mentions.sources.length > 0 && onSourcesChange) {
      const newSlugs = [...new Set([...optimisticSourceSlugs, ...mentions.sources])]
      if (newSlugs.length > optimisticSourceSlugs.length) {
        setOptimisticSourceSlugs(newSlugs)
        onSourcesChange(newSlugs)
      }
    }

    // When plan mode is active, wrap the user's message with planning instructions
    const finalMessage = planModeActive
      ? `${input.trim()}\n\n---\nBefore executing, please investigate this task thoroughly, ask me clarifying questions if needed, then create a phased implementation plan with a task checklist and submit it via SubmitPlan before making any changes.`
      : input.trim()

    const pendingAttachments = attachments
    const submitResult = onSubmit(
      finalMessage,
      attachments.length > 0 ? attachments : undefined,
      resolvedSkillSlugs.length > 0 ? resolvedSkillSlugs : undefined,
      isProcessing ? delivery : undefined,
    )
    setInput('')
    setAttachments([])
    if (planModeActive) setPlanModeActive(false)
    // Clear draft immediately (cancel any pending debounced sync)
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current)
    onInputChange?.('')
    prevInputValueRef.current = ''

    void Promise.resolve(submitResult).catch(() => {
      setAttachments(pendingAttachments)
    })

    // Restore focus after state updates
    requestAnimationFrame(() => {
      richInputRef.current?.focus()
    })

    return true
    // eslint-disable-next-line react-hooks/exhaustive-deps -- richInputRef is a stable ref
  }, [input, attachments, followUpItems, disabled, disableSend, isApplyingModelPreset, isProcessing, onInputChange, onSubmit, skills, sources, optimisticSourceSlugs, onSourcesChange, onWorkingDirectoryChange, homeDir, planModeActive])

  // Listen for craft:submit-input events (simulate pressing the Send button)
  React.useEffect(() => {
    const handleSubmitInput = (e: CustomEvent<{ sessionId?: string }>) => {
      const targetSessionId = e.detail?.sessionId
      if (!shouldHandleScopedInputEvent({ sessionId, isFocusedPanel, targetSessionId })) return
      submitMessage()
    }

    window.addEventListener('craft:submit-input', handleSubmitInput as EventListener)
    return () => window.removeEventListener('craft:submit-input', handleSubmitInput as EventListener)
  }, [sessionId, isFocusedPanel, submitMessage])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    submitMessage(isProcessing ? 'steer' : undefined)
  }

  const handleStop = (silent = false) => {
    onStop?.(silent)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // During IME composition, ESC should cancel composition, not trigger app/menu ESC behavior.
    if (e.key === 'Escape' && e.nativeEvent.isComposing) {
      return
    }

    // Don't submit when mention menu is open AND has visible content
    if (inlineMention.isOpen) {
      // Only intercept navigation/selection keys if menu actually shows items or is loading
      const hasVisibleContent = inlineMention.sections.some(s => s.items.length > 0) || inlineMention.isSearching
      if (hasVisibleContent && (e.key === 'Enter' || e.key === 'Tab' || e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        // These keys are handled by the InlineMentionMenu component
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        inlineMention.close()
        return
      }
    }

    // Don't submit when slash command menu is open - let it handle the Enter key
    if (inlineSlash.isOpen) {
      if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        // These keys are handled by the InlineSlashCommand component
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        inlineSlash.close()
        return
      }
    }

    // Don't submit when label menu is open - let it handle navigation keys
    if (inlineLabel.isOpen) {
      if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        inlineLabel.close()
        return
      }
    }

    // Skip submission during IME composition - user is confirming composed characters, not sending
    // Handle send key based on user preference:
    // - 'enter': Enter sends (Shift+Enter for newline). While a turn is running,
    //   Enter steers and ⌘/Ctrl+Enter stops the turn and sends.
    // - 'cmd-enter': ⌘/Ctrl+Enter sends (Enter for newline). While a turn is
    //   running, that chord steers and ⌘/Ctrl+Shift+Enter stops and sends.
    const commandChord = e.metaKey || e.ctrlKey
    if (sendMessageKey === 'enter') {
      if (e.key === 'Enter' && !e.shiftKey && !commandChord && !e.nativeEvent.isComposing) {
        e.preventDefault()
        submitMessage(isProcessing ? 'steer' : undefined)
      }
      if (e.key === 'Enter' && commandChord && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault()
        submitMessage(isProcessing ? 'interrupt' : undefined)
      }
    } else if (e.key === 'Enter' && commandChord && !e.nativeEvent.isComposing) {
      if (e.shiftKey && isProcessing) {
        e.preventDefault()
        submitMessage('interrupt')
      } else if (!e.shiftKey) {
        e.preventDefault()
        submitMessage(isProcessing ? 'steer' : undefined)
      }
    }
    // Cmd/Ctrl+Shift+M: toggle voice input
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'm') {
      e.preventDefault()
      handleMicClick()
      return
    }

    if (e.key === 'Escape') {
      // Skip blur if a popover/overlay is open — let the overlay handle ESC instead.
      // This prevents the input from consuming ESC when focus gets pulled back here
      // while a popover is still visible (portal DOM isolation means the event won't
      // reach the popover's DismissableLayer otherwise).
      if (!hasOpenOverlay()) {
        richInputRef.current?.blur()
      }
    }
  }

  // Plan mode: toggle active state (brain button)
  const handlePlanClick = React.useCallback(() => {
    if (isProcessing || disabled) return
    setPlanModeActive(prev => !prev)
    requestAnimationFrame(() => {
      richInputRef.current?.focus()
    })
  }, [isProcessing, disabled, richInputRef])

  // Speech-to-text: handle mic button click
  const handleMicClick = React.useCallback(async () => {
    const result = await toggleRecording()
    if (result) {
      // Insert transcribed text at current cursor position or append
      const currentValue = input
      const cursorPos = richInputRef.current?.selectionStart ?? currentValue.length
      const before = currentValue.slice(0, cursorPos)
      const after = currentValue.slice(cursorPos)
      const separator = before.length > 0 && !before.endsWith(' ') && !before.endsWith('\n') ? ' ' : ''
      const newValue = before + separator + result + after
      setInput(newValue)
      syncToParent(newValue)
      setTimeout(() => richInputRef.current?.focus(), 0)
    }
  }, [toggleRecording, input, syncToParent, richInputRef])

  // Show STT errors via toast
  React.useEffect(() => {
    if (sttError) {
      toast.error(sttError)
    }
  }, [sttError])

  // Handle input changes from RichTextInput
  const handleInputChange = React.useCallback((value: string) => {
    // Get previous input value before updating state
    const prevValue = inputRef.current

    setInput(value)
    syncToParent(value) // Debounced sync to parent for draft persistence

    // Sync source selection when mentions are removed from input
    if (onSourcesChange) {
      const sourceSlugs = sources.map(s => s.config.slug)

      // Parse mentions from previous and current input
      const prevMentions = parseMentions(prevValue, [], sourceSlugs)
      const currMentions = parseMentions(value, [], sourceSlugs)

      // Remove sources that were mentioned before but not anymore
      const removedSources = prevMentions.sources.filter(slug => !currMentions.sources.includes(slug))
      if (removedSources.length > 0) {
        const newSlugs = optimisticSourceSlugs.filter(slug => !removedSources.includes(slug))
        setOptimisticSourceSlugs(newSlugs)
        onSourcesChange(newSlugs)
      }
    }
  }, [syncToParent, sources, optimisticSourceSlugs, onSourcesChange])

  // Handle input with cursor position (for menu detection)
  const handleRichInput = React.useCallback((value: string, cursorPosition: number) => {
    // Update inline slash command state
    inlineSlash.handleInputChange(value, cursorPosition)

    // Update inline mention state (for @mentions - skills, sources, folders)
    inlineMention.handleInputChange(value, cursorPosition)

    // Update inline label state (for #labels)
    inlineLabel.handleInputChange(value, cursorPosition)

    // Auto-capitalize first letter (but not for slash commands, @mentions, or #labels)
    // Only if autoCapitalisation setting is enabled
    let newValue = value
    if (autoCapitalisation && value.length > 0 && value.charAt(0) !== '/' && value.charAt(0) !== '@' && value.charAt(0) !== '#') {
      const capitalizedFirst = value.charAt(0).toUpperCase()
      if (capitalizedFirst !== value.charAt(0)) {
        newValue = capitalizedFirst + value.slice(1)
        // Set cursor position BEFORE state update so it's used when useEffect syncs the value
        richInputRef.current?.setSelectionRange(cursorPosition, cursorPosition)
        setInput(newValue)
        syncToParent(newValue)
        return
      }
    }

    // Apply smart typography (-> to →, etc.)
    const typography = applySmartTypography(value, cursorPosition)
    if (typography.replaced) {
      newValue = typography.text
      // Set cursor position BEFORE state update so it's used when useEffect syncs the value
      richInputRef.current?.setSelectionRange(typography.cursor, typography.cursor)
      setInput(newValue)
      syncToParent(newValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- richInputRef is a stable ref
  }, [inlineSlash, inlineMention, inlineLabel, syncToParent, autoCapitalisation])

  // Handle inline slash command selection (removes the /command text)
  const handleInlineSlashCommandSelect = React.useCallback((commandId: SlashCommandId) => {
    const newValue = inlineSlash.handleSelectCommand(commandId)
    setInput(newValue)
    syncToParent(newValue)
    richInputRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- richInputRef is a stable ref
  }, [inlineSlash, syncToParent])

  // Handle inline slash folder selection (inserts a directory badge)
  const handleInlineSlashFolderSelect = React.useCallback((path: string) => {
    const newValue = inlineSlash.handleSelectFolder(path)
    setInput(newValue)
    syncToParent(newValue)
    richInputRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- richInputRef is a stable ref
  }, [inlineSlash, syncToParent])

  // Handle inline mention selection (inserts appropriate mention text)
  const handleInlineMentionSelect = React.useCallback((item: MentionItem) => {
    const { value: newValue, cursorPosition } = inlineMention.handleSelect(item)
    setInput(newValue)
    syncToParent(newValue)
    // Focus input and restore cursor position after badge renders
    setTimeout(() => {
      richInputRef.current?.focus()
      richInputRef.current?.setSelectionRange(cursorPosition, cursorPosition)
    }, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- richInputRef is a stable ref
  }, [inlineMention, syncToParent])

  // Handle inline label selection (removes the #label text from input)
  const handleInlineLabelSelect = React.useCallback((labelId: string) => {
    const newValue = inlineLabel.handleSelect(labelId)
    setInput(newValue)
    syncToParent(newValue)
    richInputRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- richInputRef is a stable ref
  }, [inlineLabel, syncToParent])

  // Handle inline state selection from # menu (removes #text, changes session state)
  const handleInlineStateSelect = React.useCallback((stateId: string) => {
    const newValue = inlineLabel.handleSelect('')
    setInput(newValue)
    syncToParent(newValue)
    if (sessionId) {
      onSessionStatusChange?.(sessionId, stateId)
    }
    richInputRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- richInputRef is a stable ref
  }, [inlineLabel, syncToParent, sessionId, onSessionStatusChange])

  const followUpLayoutKey = React.useMemo(
    () => followUpItems.map(item => [
      item.id,
      item.index ?? '',
      item.noteLabel,
      item.selectedText,
      item.color ?? '',
    ].join('::')).join('|'),
    [followUpItems]
  )
  const previousFollowUpLayoutKeyRef = React.useRef<string | null>(null)
  const [animateFollowUpLayout, setAnimateFollowUpLayout] = React.useState(false)

  React.useEffect(() => {
    const previous = previousFollowUpLayoutKeyRef.current
    previousFollowUpLayoutKeyRef.current = followUpLayoutKey

    if (previous == null || previous === followUpLayoutKey) return

    setAnimateFollowUpLayout(true)
    const timer = window.setTimeout(() => {
      setAnimateFollowUpLayout(false)
    }, 220)

    return () => window.clearTimeout(timer)
  }, [followUpLayoutKey])

  const hasContent = input.trim() || attachments.length > 0 || followUpItems.length > 0

  return (
    <form onSubmit={handleSubmit}>
      {/* Hidden file input for browser-based file picker */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="*/*"
        className="hidden"
        onChange={handleFileInputChange}
      />
      <div
        ref={containerRef}
        className={cn(
          'overflow-hidden transition-all',
          // Container styling - only when not wrapped by InputContainer
          !unstyled && 'rounded-[16px] shadow-middle',
          !unstyled && 'bg-background',
          isDraggingOver && 'ring-2 ring-foreground ring-offset-2 ring-offset-background bg-foreground/5'
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Inline Slash Command Autocomplete */}
        <InlineSlashCommand
          open={inlineSlash.isOpen}
          onOpenChange={(open) => !open && inlineSlash.close()}
          sections={inlineSlash.sections}
          activeCommands={activeCommands}
          onSelectCommand={handleInlineSlashCommandSelect}
          onSelectFolder={handleInlineSlashFolderSelect}
          filter={inlineSlash.filter}
          position={inlineSlash.position}
        />

        {/* Inline Mention Autocomplete (skills, sources, files) */}
        <InlineMentionMenu
          open={inlineMention.isOpen}
          onOpenChange={(open) => !open && inlineMention.close()}
          sections={inlineMention.sections}
          onSelect={handleInlineMentionSelect}
          filter={inlineMention.filter}
          position={inlineMention.position}
          workspaceId={workspaceId}
          maxWidth={280}
          isSearching={inlineMention.isSearching}
        />

        {/* Inline Label & State Autocomplete (#labels / #states) */}
        <InlineLabelMenu
          open={inlineLabel.isOpen}
          onOpenChange={(open) => !open && inlineLabel.close()}
          items={inlineLabel.items}
          onSelect={handleInlineLabelSelect}
          onAddLabel={handleAddLabel}
          filter={inlineLabel.filter}
          position={inlineLabel.position}
          states={inlineLabel.states}
          activeStateId={inlineLabel.activeStateId}
          onSelectState={handleInlineStateSelect}
        />

        {/* Controlled EditPopover for "Add New Label" — opens when user selects
            the option from the # menu with no matches */}
        {addLabelEditConfig && (
          <EditPopover
            trigger={<span className="absolute top-0 left-0 w-0 h-0 overflow-hidden" />}
            open={addLabelPopoverOpen}
            onOpenChange={setAddLabelPopoverOpen}
            {...addLabelEditConfig}
            defaultValue={addLabelPrefill}
            secondaryAction={workspaceRootPath ? {
              label: 'Edit File',
              filePath: `${workspaceRootPath}/labels/config.json`,
            } : undefined}
            side="top"
            align="start"
          />
        )}

        {/* Attachment Preview */}
        <AttachmentPreview
          attachments={attachments}
          onRemove={handleRemoveAttachment}
          disabled={disabled}
          loadingCount={loadingCount}
        />

        {/* Follow-up context chips */}
        <AnimatePresence initial={false}>
          {followUpItems.length > 0 && (
            <motion.div
              key="follow-up-chips"
              layout={animateFollowUpLayout}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.18, ease: [0.2, 0, 0.2, 1] }}
              className="overflow-hidden"
            >
              <motion.div layout={animateFollowUpLayout} className="px-3 pt-3.5 pb-0">
                <motion.div layout={animateFollowUpLayout} className="flex flex-wrap gap-1">
                  <AnimatePresence initial={false}>
                    {followUpItems.map((item, idx) => {
                      const chipIndex = item.index ?? idx + 1
                      const tooltipText = item.selectedText.trim() || 'Selected text'
                      const selectedExcerpt = formatFollowUpChipText(item.selectedText, 'Selected text', 50)
                      const noteExcerpt = formatFollowUpChipText(item.noteLabel, 'Follow-up', 50)

                      return (
                        <motion.button
                          key={item.id}
                          type="button"
                          layout={animateFollowUpLayout}
                          initial={{ opacity: 0, y: 6, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -4, scale: 0.98 }}
                          transition={{ duration: 0.16, ease: [0.2, 0, 0.2, 1] }}
                          className="inline-flex max-w-full items-center gap-1.5 overflow-hidden rounded-[6px] bg-foreground/2 pl-1.5 pr-2 py-1 text-[13px] text-foreground/80 select-none transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          onClick={(event) => {
                            const rect = event.currentTarget.getBoundingClientRect()
                            onFollowUpClick?.(item, {
                              x: rect.left + rect.width / 2,
                              y: rect.top - 8,
                            })
                          }}
                        >
                          <Tooltip delayDuration={250}>
                            <TooltipTrigger asChild>
                              <span
                                role="button"
                                tabIndex={0}
                                className="inline-flex h-4 min-w-4 cursor-pointer items-center justify-center rounded-[4px] bg-background px-0.5 text-[10px] font-medium text-foreground shadow-minimal focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                onMouseDown={(event) => {
                                  event.preventDefault()
                                  event.stopPropagation()
                                }}
                                onClick={(event) => {
                                  event.preventDefault()
                                  event.stopPropagation()
                                  onFollowUpIndexClick?.(item)
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    onFollowUpIndexClick?.(item)
                                  }
                                }}
                              >
                                {chipIndex}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[420px] break-words text-xs">
                              {tooltipText}
                            </TooltipContent>
                          </Tooltip>
                          <span className="min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap pr-0.5 text-left">
                            <span className="italic text-foreground/60">{selectedExcerpt}</span>
                            <span className="mx-1 text-foreground/40">·</span>
                            <span>{noteExcerpt}</span>
                          </span>
                        </motion.button>
                      )
                    })}
                  </AnimatePresence>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Plan mode indicator */}
        <AnimatePresence>
          {planModeActive && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden"
            >
              <div className="flex items-center gap-1.5 px-5 pt-2.5 pb-0">
                <Brain className="h-3 w-3 text-accent shrink-0" />
                <span className="text-[11px] text-accent font-medium">Plan mode</span>
                <span className="text-[11px] text-muted-foreground">— describe your task, the agent will plan before executing</span>
                <button
                  type="button"
                  onClick={() => setPlanModeActive(false)}
                  className="ml-auto text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Rich Text Input with inline mention badges */}
        {/* In compact mode, hide input while processing (collapses to just bottom bar) */}
        {!(compactMode && isProcessing) && (
        <RichTextInput
          ref={richInputRef}
          value={input}
          onChange={handleInputChange}
          onInput={handleRichInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onLongTextPaste={handleLongTextPaste}
          onFocus={() => { setIsFocused(true); onFocusChange?.(true) }}
          onBlur={() => {
            // Save caret position before losing focus (for restoration via craft:focus-input)
            lastCaretPositionRef.current = richInputRef.current?.selectionStart ?? null
            setIsFocused(false)
            onFocusChange?.(false)
          }}
          placeholder={effectivePlaceholder}
          disabled={disabled}
          skills={skills}
          sources={sources}
          workspaceId={workspaceSlug}
          className="pl-5 pr-4 pt-4 pb-3 overflow-y-auto min-h-[88px]"
          style={{ maxHeight: inputMaxHeight }}
          data-tutorial="chat-input"
          spellCheck={spellCheck}
        />
        )}

        {/* Bottom Row: Controls - wrapped in relative container for status slot overlay */}
        <div className="relative">
          {/* Status slot overlay - escape interrupt (highest priority), browser status, etc. */}
          <ToolbarStatusSlot
            showEscapeOverlay={isProcessing && showEscapeOverlay}
            sessionId={sessionId}
          />

          <div className={cn("flex items-center gap-1 px-2 py-2", !compactMode && "border-t border-border/50")}>
          {/* Left side: Context badges - shrinkable so model + send always stay visible */}
          {/* Hidden in compact mode (EditPopover embedding) */}
          {!compactMode && (
          <div className="flex items-center gap-1 min-w-32 shrink overflow-hidden">
          {/* 1. Attach Files Badge */}
          <FreeFormInputContextBadge
            icon={<Paperclip className="h-4 w-4" />}
            // Show count ("1 file" / "X files") instead of filename for cleaner UI
            label={attachments.length > 0
              ? attachments.length === 1
                ? "1 file"
                : `${attachments.length} files`
              : "Attach Files"
            }
            isExpanded={isEmptySession}
            hasSelection={attachments.length > 0}
            showChevron={false}
            onClick={handleAttachClick}
            tooltip="Attach files"
            disabled={disabled}
          />

          {/* 2. Source Selector Badge - only show if onSourcesChange is provided */}
          {onSourcesChange && (
            <div className="relative shrink min-w-0 overflow-hidden">
              <FreeFormInputContextBadge
                buttonRef={sourceButtonRef}
                icon={
                  optimisticSourceSlugs.length === 0 ? (
                    <DatabaseZap className="h-4 w-4" />
                  ) : (
                    <div className="flex items-center -ml-0.5">
                      {(() => {
                        const enabledSources = sources.filter(s => optimisticSourceSlugs.includes(s.config.slug))
                        const displaySources = enabledSources.slice(0, 3)
                        const remainingCount = enabledSources.length - 3
                        return (
                          <>
                            {displaySources.map((source, index) => (
                              <div
                                key={source.config.slug}
                                className={cn("relative h-5 w-5 rounded-[4px] bg-background shadow-minimal flex items-center justify-center", index > 0 && "-ml-1")}
                                style={{ zIndex: index + 1 }}
                              >
                                <SourceAvatar source={source} size="xs" />
                              </div>
                            ))}
                            {remainingCount > 0 && (
                              <div
                                className="-ml-1 h-5 w-5 rounded-[4px] bg-background shadow-minimal flex items-center justify-center text-[8px] font-medium text-muted-foreground"
                                style={{ zIndex: displaySources.length + 1 }}
                              >
                                +{remainingCount}
                              </div>
                            )}
                          </>
                        )
                      })()}
                    </div>
                  )
                }
                label={
                  optimisticSourceSlugs.length === 0
                    ? "Choose Sources"
                    : (() => {
                        const enabledSources = sources.filter(s => optimisticSourceSlugs.includes(s.config.slug))
                        if (enabledSources.length === 1) return enabledSources[0].config.name
                        if (enabledSources.length === 2) return enabledSources.map(s => s.config.name).join(', ')
                        return `${enabledSources.length} sources`
                      })()
                }
                isExpanded={isEmptySession}
                hasSelection={optimisticSourceSlugs.length > 0}
                showChevron={true}
                isOpen={sourceDropdownOpen}
                disabled={disabled}
                data-tutorial="source-selector-button"
                onClick={() => setSourceDropdownOpen(prev => !prev)}
                tooltip="Sources"
              />

              <SourceSelectorPopover
                open={sourceDropdownOpen}
                onOpenChange={setSourceDropdownOpen}
                anchorRef={sourceButtonRef}
                sources={sources}
                selectedSlugs={optimisticSourceSlugs}
                onToggleSlug={(slug) => {
                  const isEnabled = optimisticSourceSlugs.includes(slug)
                  const newSlugs = isEnabled
                    ? optimisticSourceSlugs.filter(currentSlug => currentSlug !== slug)
                    : [...optimisticSourceSlugs, slug]
                  setOptimisticSourceSlugs(newSlugs)
                  onSourcesChange?.(newSlugs)
                }}
              />
            </div>
          )}

          {/* 3. Skill Picker Badge */}
          {skills.length > 0 && (
            <Popover open={skillDropdownOpen} onOpenChange={setSkillDropdownOpen}>
              <PopoverTrigger asChild>
                <span className="shrink min-w-0 overflow-hidden">
                  <FreeFormInputContextBadge
                    icon={<Zap className="h-4 w-4" />}
                    label="Choose Skill"
                    isExpanded={isEmptySession}
                    hasSelection={false}
                    showChevron={true}
                    isOpen={skillDropdownOpen}
                    disabled={disabled}
                    tooltip="Skills"
                  />
                </span>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0 border-0 shadow-none bg-transparent"
                side="top"
                align="start"
                sideOffset={8}
                onCloseAutoFocus={(e) => {
                  e.preventDefault()
                  window.dispatchEvent(new CustomEvent('craft:focus-input'))
                }}
              >
                <CommandPrimitive
                  className="min-w-[220px] overflow-hidden rounded-[8px] bg-background text-foreground shadow-modal-small"
                >
                  <div className="border-b border-border/50 px-3 py-2">
                    <CommandPrimitive.Input
                      placeholder="Filter skills..."
                      className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/50"
                      autoFocus={!isIPad}
                    />
                  </div>
                  <CommandPrimitive.List className="max-h-[240px] overflow-y-auto p-1 [&_[cmdk-list-sizer]]:space-y-px">
                    <CommandPrimitive.Empty className="py-3 text-center text-sm text-muted-foreground">
                      No skills found
                    </CommandPrimitive.Empty>
                    {skills.map((skill) => (
                      <CommandPrimitive.Item
                        key={skill.slug}
                        value={skill.metadata.name}
                        onSelect={() => {
                          const pluginName = skill.source === 'workspace' ? workspaceSlug : AGENTS_PLUGIN_NAME
                          const qualifiedName = pluginName ? `${pluginName}:${skill.slug}` : skill.slug
                          const mention = `[skill:${qualifiedName}] `
                          const currentInput = input
                          const separator = currentInput.length > 0 && !currentInput.endsWith(' ') ? ' ' : ''
                          const newValue = currentInput + separator + mention
                          setInput(newValue)
                          onInputChange?.(newValue)
                          setSkillDropdownOpen(false)
                        }}
                        className="flex cursor-pointer select-none items-center gap-3 rounded-[6px] px-3 py-1.5 text-[13px] outline-none data-[selected=true]:bg-foreground/3"
                      >
                        <SkillAvatar skill={skill} size="sm" workspaceId={workspaceSlug} />
                        <div className="flex-1 min-w-0 truncate">{skill.metadata.name}</div>
                      </CommandPrimitive.Item>
                    ))}
                  </CommandPrimitive.List>
                </CommandPrimitive>
              </PopoverContent>
            </Popover>
          )}

          {/* 4. Working Directory Selector Badge — hidden on remote client (no local filesystem access) */}

          {/* 5. Open Folder Button — hidden on remote client (no local filesystem access) */}
          </div>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right side: Send - never shrink so they're always visible */}
          <div className="flex items-center shrink-0 gap-1">
          {/* Curated provider/model selector — only available before the first turn. */}
          {isEmptySession && availableManagedPresets.length > 0 && (
            <DropdownMenu open={modelDropdownOpen} onOpenChange={setModelDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  disabled={disabled || isApplyingModelPreset}
                  className="h-7 max-w-[190px] px-2 rounded-[6px] flex items-center gap-1.5 text-xs text-muted-foreground hover:bg-foreground/5 hover:text-foreground disabled:opacity-50 transition-colors"
                  aria-label="Choose model provider"
                >
                  {isApplyingModelPreset ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Brain className="h-3.5 w-3.5" />}
                  <span className="truncate">{selectedManagedPreset?.label ?? currentModelDisplayName}</span>
                  <ChevronDown className="h-3 w-3 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <StyledDropdownMenuContent align="end" side="top" className="min-w-[250px]">
                {availableManagedPresets.map((preset) => {
                  const isSelected = preset.id === selectedManagedPreset?.id
                  return (
                    <StyledDropdownMenuItem
                      key={preset.id}
                      onClick={() => void applyManagedModelPreset(preset.id)}
                      className="gap-3"
                    >
                      <div className="flex h-7 w-7 items-center justify-center rounded-md bg-foreground/5">
                        <ConnectionIcon connection={preset.connection} size={14} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium">{preset.label}</div>
                        <div className="text-[11px] text-muted-foreground">{preset.providerLabel} · {preset.effortLabel}</div>
                      </div>
                      {isSelected && <Check className="h-4 w-4" />}
                    </StyledDropdownMenuItem>
                  )
                })}
              </StyledDropdownMenuContent>
            </DropdownMenu>
          )}

          {/* 5.5 Context Usage Ring - always visible and compacts on click */}
          {(() => {
            const effectiveContextWindow = getModelContextWindow(currentModel) || contextStatus?.contextWindow
            const currentInputTokens = contextStatus?.inputTokens || 0
            const usagePercent = effectiveContextWindow
              ? Math.min(100, Math.round((currentInputTokens / effectiveContextWindow) * 100))
              : 0
            const tooltipText = effectiveContextWindow
              ? `${usagePercent}% · ${formatTokenCount(currentInputTokens)} / ${formatTokenCount(effectiveContextWindow)} context used`
              : 'Context usage'
            const tooltipContent = isProcessing
              ? `${tooltipText} — wait for current operation`
              : `${tooltipText} — click to compact`
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => onSubmit('/compact', [])}
                    disabled={isProcessing}
                    className="h-7 w-7 mr-1 rounded-[6px] shrink-0 cursor-pointer flex items-center justify-center transition-colors hover:bg-foreground/5 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={tooltipContent}
                  >
                    <ContextRing
                      inputTokens={currentInputTokens}
                      contextWindow={effectiveContextWindow || 0}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {tooltipContent}
                </TooltipContent>
              </Tooltip>
            )
          })()}

          {/* 5.5b Plan Button - toggle plan mode */}
          {!compactMode && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className={cn(
                    "h-7 w-7 rounded-[6px] shrink-0 transition-colors",
                    planModeActive && "bg-accent/15 text-accent hover:bg-accent/20"
                  )}
                  onClick={handlePlanClick}
                  disabled={disabled || isProcessing}
                >
                  <Brain className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {planModeActive ? 'Plan mode active — click to disable' : 'Plan before executing'}
              </TooltipContent>
            </Tooltip>
          )}

          {/* 5.75 Mic Button - Speech to text */}
          {!compactMode && (
            <>
            {isRecording && (
              <span className="text-xs text-destructive font-mono tabular-nums select-none">
                {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}
              </span>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant={isRecording ? "destructive" : "ghost"}
                  className={cn(
                    "h-7 w-7 rounded-[6px] shrink-0",
                    isRecording && "animate-pulse",
                    isTranscribing && "opacity-50 pointer-events-none",
                  )}
                  onClick={handleMicClick}
                  disabled={disabled || isTranscribing || isProcessing}
                >
                  {isTranscribing ? (
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mic className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {isRecording
                  ? "Stop recording"
                  : isTranscribing
                    ? "Transcribing..."
                    : `Voice input (${cmdKey}+Shift+M)`
                }
              </TooltipContent>
            </Tooltip>
            </>
          )}

          {/* 6. Send / steer / stop. Stop stays available so a draft can be discarded. */}
          {isProcessing && hasContent && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="submit"
                    className="h-7 rounded-full px-2.5 text-xs shrink-0"
                    disabled={disabled || disableSend || isApplyingModelPreset}
                  >
                    Steer
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {sendMessageKey === 'enter'
                    ? 'Send into the current turn (Enter)'
                    : `Send into the current turn (${cmdKey}Enter)`}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-7 rounded-full px-2.5 text-xs shrink-0"
                    disabled={disabled || disableSend || isApplyingModelPreset}
                    onClick={() => submitMessage('interrupt')}
                  >
                    Stop & send
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {sendMessageKey === 'enter'
                    ? `Stop the current turn and send (${cmdKey}Enter)`
                    : `Stop the current turn and send (${cmdKey}⇧Enter)`}
                </TooltipContent>
              </Tooltip>
            </>
          )}
          {isProcessing ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="h-7 w-7 rounded-full shrink-0 hover:bg-foreground/15 active:bg-foreground/20"
                  onClick={() => handleStop(false)}
                  aria-label="Stop"
                >
                  <Square className="h-3 w-3 fill-current" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">Stop</TooltipContent>
            </Tooltip>
          ) : (
            <Button
              type="submit"
              size="icon"
              className="h-7 w-7 rounded-full shrink-0"
              disabled={!hasContent || disabled || disableSend || isApplyingModelPreset}
              data-tutorial="send-button"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          )}
          </div>
          </div>
        </div>
      </div>
    </form>
  )
}

/**
 * Format path for display, with home directory shortened
 */
function formatPathForDisplay(path: string, homeDir: string): string {
  let displayPath = path
  if (homeDir && path.startsWith(homeDir)) {
    const relativePath = path.slice(homeDir.length)
    // Remove leading separator if present, show root separator if empty
    displayPath = relativePath.startsWith(PATH_SEP)
      ? relativePath.slice(1)
      : (relativePath || PATH_SEP)
  }
  return `in ${displayPath}`
}

/**
 * WorkingDirectoryBadge - Context badge for selecting working directory
 * Uses cmdk for filterable folder list when there are more than 5 recent folders.
 */
function WorkingDirectoryBadge({
  workingDirectory,
  onWorkingDirectoryChange,
  sessionFolderPath,
  isEmptySession = false,
}: {
  workingDirectory?: string
  onWorkingDirectoryChange: (path: string) => void
  sessionFolderPath?: string
  isEmptySession?: boolean
}) {
  const [recentDirs, setRecentDirs] = React.useState<string[]>([])
  const [popoverOpen, setPopoverOpen] = React.useState(false)
  const [homeDir, setHomeDir] = React.useState<string>('')
  const [gitBranch, setGitBranch] = React.useState<string | null>(null)
  const [filter, setFilter] = React.useState('')
  const inputRef = React.useRef<HTMLInputElement>(null)

  // Load home directory and recent directories on mount
  React.useEffect(() => {
    setRecentDirs(getRecentWorkingDirs())
    window.electronAPI?.getHomeDir?.().then((dir: string) => {
      if (dir) setHomeDir(dir)
    }).catch(() => {})
  }, [])

  // Fetch git branch when working directory changes
  React.useEffect(() => {
    if (workingDirectory) {
      window.electronAPI?.getGitBranch?.(workingDirectory).then((branch: string | null) => {
        setGitBranch(branch)
      }).catch(() => {})
    } else {
      setGitBranch(null)
    }
  }, [workingDirectory])

  // Reset filter, refresh history, and focus input when popover opens
  React.useEffect(() => {
    if (popoverOpen) {
      setFilter('')
      setRecentDirs(getRecentWorkingDirs())
      // Focus input after popover animation completes (only if filter is shown)
      const timer = setTimeout(() => {
        if (!isIPad) inputRef.current?.focus()
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [popoverOpen])

  const handleChooseFolder = async () => {
    if (!window.electronAPI || isIPad) return
    setPopoverOpen(false)
    const selectedPath = await window.electronAPI.openFolderDialog()
    if (selectedPath) {
      setRecentDirs(addRecentWorkingDir(selectedPath))
      onWorkingDirectoryChange(selectedPath)
    }
  }

  const handleSelectRecent = (path: string) => {
    setRecentDirs(addRecentWorkingDir(path)) // Move to top of recent list
    onWorkingDirectoryChange(path)
    setPopoverOpen(false)
  }

  const handleRemoveRecent = (e: React.MouseEvent, path: string) => {
    e.stopPropagation() // Don't trigger the item's onSelect
    setRecentDirs(removeRecentWorkingDir(path))
  }

  const handleReset = () => {
    if (sessionFolderPath) {
      onWorkingDirectoryChange(sessionFolderPath)
      setPopoverOpen(false)
    }
  }

  // Filter out current directory from recent list and sort alphabetically by folder name
  const filteredRecent = recentDirs
    .filter(p => p !== workingDirectory)
    .sort((a, b) => {
      const nameA = getPathBasename(a).toLowerCase()
      const nameB = getPathBasename(b).toLowerCase()
      return nameA.localeCompare(nameB)
    })
  // Show filter input only when more than 5 recent folders
  const showFilter = filteredRecent.length > 5

  // Determine label - "Work in Folder" if not set or at session root, otherwise folder name
  const hasFolder = !!workingDirectory && workingDirectory !== sessionFolderPath
  const folderName = hasFolder ? (getPathBasename(workingDirectory) || 'Folder') : 'Work in Folder'

  // Show reset option when a folder is selected and it differs from session folder
  const showReset = hasFolder && sessionFolderPath && sessionFolderPath !== workingDirectory

  // Styles matching todo-filter-menu.tsx for consistency
  const MENU_CONTAINER_STYLE = 'min-w-[200px] max-w-[400px] overflow-hidden rounded-[8px] bg-background text-foreground shadow-modal-small p-0'
  const MENU_LIST_STYLE = 'max-h-[200px] overflow-y-auto p-1 [&_[cmdk-list-sizer]]:space-y-px'
  const MENU_ITEM_STYLE = 'flex cursor-pointer select-none items-center gap-2 rounded-[6px] px-3 py-1.5 text-[13px] outline-none'

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <span className="shrink min-w-0 overflow-hidden">
          <FreeFormInputContextBadge
            icon={<Icon_Home className="h-4 w-4" />}
            label={folderName}
            isExpanded={isEmptySession}
            hasSelection={hasFolder}
            showChevron={true}
            isOpen={popoverOpen}
            tooltip={
              hasFolder ? (
                <span className="flex flex-col gap-0.5">
                  <span className="font-medium">Working directory</span>
                  <span className="text-xs opacity-70">{formatPathForDisplay(workingDirectory, homeDir)}</span>
                  {gitBranch && <span className="text-xs opacity-70">on {gitBranch}</span>}
                </span>
              ) : "Choose working directory"
            }
          />
        </span>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" sideOffset={8} className={MENU_CONTAINER_STYLE}>
        <CommandPrimitive shouldFilter={showFilter}>
          {/* Filter input - only shown when more than 5 recent folders */}
          {showFilter && (
            <div className="border-b border-border/50 px-3 py-2">
              <CommandPrimitive.Input
                ref={inputRef}
                value={filter}
                onValueChange={setFilter}
                placeholder="Filter folders..."
                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/50 placeholder:select-none"
              />
            </div>
          )}

          <CommandPrimitive.List className={MENU_LIST_STYLE}>
            {/* Current Folder Display - shown at top with checkmark */}
            {hasFolder && (
              <CommandPrimitive.Item
                value={`current-${workingDirectory}`}
                className={cn(MENU_ITEM_STYLE, 'pointer-events-none bg-foreground/5')}
                disabled
              >
                <Icon_Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="flex-1 min-w-0 truncate">
                  <span>{folderName}</span>
                  <span className="text-muted-foreground ml-1.5">{formatPathForDisplay(workingDirectory, homeDir)}</span>
                </span>
                <Check className="h-4 w-4 shrink-0" />
              </CommandPrimitive.Item>
            )}

            {/* Separator after current folder */}
            {hasFolder && filteredRecent.length > 0 && (
              <div className="h-px bg-border my-1 mx-1" />
            )}

            {/* Recent Directories - filterable (current directory already filtered out via filteredRecent) */}
            {filteredRecent.map((path) => {
              const recentFolderName = getPathBasename(path) || 'Folder'
              return (
                <CommandPrimitive.Item
                  key={path}
                  value={`${recentFolderName} ${path}`}
                  onSelect={() => handleSelectRecent(path)}
                  className={cn(MENU_ITEM_STYLE, 'group/item data-[selected=true]:bg-foreground/5')}
                >
                  <Icon_Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 min-w-0 truncate">
                    <span>{recentFolderName}</span>
                    <span className="text-muted-foreground ml-1.5">{formatPathForDisplay(path, homeDir)}</span>
                  </span>
                  <button
                    type="button"
                    onClick={(e) => handleRemoveRecent(e, path)}
                    className="shrink-0 h-3 w-3 rounded-[3px] flex items-center justify-center opacity-0 group-hover/item:opacity-100 text-muted-foreground hover:text-foreground hover:bg-foreground/10 transition-all"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </CommandPrimitive.Item>
              )
            })}

            {/* Empty state when filtering */}
            {showFilter && (
              <CommandPrimitive.Empty className="py-3 text-center text-sm text-muted-foreground">
                No folders found
              </CommandPrimitive.Empty>
            )}
          </CommandPrimitive.List>

          {/* Bottom actions - always visible, outside scrollable area */}
          <div className="border-t border-border/50 p-1">
            {/* Hidden on remote client — can't access desktop filesystem */}
            {!isIPad && (
            <button
              type="button"
              onClick={handleChooseFolder}
              className={cn(MENU_ITEM_STYLE, 'w-full hover:bg-foreground/5')}
            >
              Choose Folder...
            </button>
            )}
            {showReset && (
              <button
                type="button"
                onClick={handleReset}
                className={cn(MENU_ITEM_STYLE, 'w-full hover:bg-foreground/5')}
              >
                Reset
              </button>
            )}
          </div>
        </CommandPrimitive>
      </PopoverContent>
    </Popover>
  )
}
